import {
    IConditionSummaryResult,
    IFormAnswerBase,
    IFormCondition,
    IFormConditionAction,
    IFormQuestion,
    IFormQuestionBase,
    IFormQuestionPresetOption,
} from './fi-form-shared';
import { FiBooleanOperator } from '../types/fi-boolean-operator';
import { FormConditionType, FormQuestionAction } from './fi-form-enums';
import { IMediaBankFileReference } from '../media-bank';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { Injectable } from '@angular/core';
import { isNullOrUndefined } from 'util';
import { AppTextService } from '@fi-shared/app-text';
import { isDefined } from '@fi-shared/utils';

@Injectable()
export class FiFormConditionHelperService {
    constructor(
        private formHelperService: FiFormHelperService,
        private appTextService: AppTextService,
    ) {}

    isTargetQuestion(question: IFormQuestionBase, conditions: IFormCondition[]): boolean {
        return !!this.getCondition(question, conditions);
    }

    isQuestionTarget(question: IFormQuestionBase, action: IFormConditionAction): boolean {
        return action.targetQuestionUid === question.uid;
    }

    isQuestionHidden(
        question: IFormQuestionBase,
        questions: IFormQuestionBase[],
        conditions: IFormCondition[],
        answers: IFormAnswerBase[],
    ): boolean {
        if (!conditions || !this.isTargetQuestion(question, conditions)) return false;

        const targetConditions = conditions.filter((condition) =>
            condition.Actions.some((action) => {
                return this.isQuestionTarget(question, action);
            }),
        );
        return targetConditions.every((condition) => {
            const targetConditionAction = condition.Actions.find((action) => this.isQuestionTarget(question, action));
            return !this.isConditionPassed(condition, questions, targetConditionAction.Action, answers);
        });
    }

    private isAllChildConditionsPassed(
        condition: IFormCondition,
        questions: IFormQuestionBase[],
        conditionAction: FormQuestionAction,
        answers: IFormAnswerBase[],
    ): boolean {
        return condition.ChildItems.every((childCondition) =>
            this.isConditionPassed(childCondition, questions, conditionAction, answers),
        );
    }

    private isAnyOfChildConditionsPassed(
        condition: IFormCondition,
        questions: IFormQuestionBase[],
        conditionAction: FormQuestionAction,
        answers: IFormAnswerBase[],
    ): boolean {
        return condition.ChildItems.some((childCondition) =>
            this.isConditionPassed(childCondition, questions, conditionAction, answers),
        );
    }

    private isConditionPassed(
        condition: IFormCondition,
        questions: IFormQuestionBase[],
        conditionAction: FormQuestionAction,
        answers: IFormAnswerBase[],
    ): boolean {
        if (condition.ChildItems.length) {
            if (condition.ConditionOperator === FiBooleanOperator.Or || isNullOrUndefined(condition.ConditionOperator))
                return this.isAnyOfChildConditionsPassed(condition, questions, conditionAction, answers);

            if (condition.ConditionOperator === FiBooleanOperator.And) {
                return conditionAction === FormQuestionAction.Show
                    ? this.isAllChildConditionsPassed(condition, questions, conditionAction, answers)
                    : this.isAnyOfChildConditionsPassed(condition, questions, conditionAction, answers);
            }
        }
        const sourceQuestion = questions.find((question) => condition.sourceQuestionUid === question.uid);
        if (!isDefined(sourceQuestion)) return true;

        const formAnswer = this.getSourceQuestionAnswer(sourceQuestion, answers);
        return this.isTargetQuestionShown(
            condition,
            sourceQuestion,
            formAnswer.Value,
            formAnswer.ValuePresetOptionIds,
            formAnswer.MediaBankFileReferences,
            conditionAction,
        );
    }

    private isTargetQuestionShown(
        condition: IFormCondition,
        sourceQuestion: IFormQuestionBase,
        answerValue: string,
        answerPresetOptionIds: number[],
        mediaBankFileReferences: IMediaBankFileReference[],
        conditionAction: FormQuestionAction,
    ): boolean {
        if (condition.ConditionType === FormConditionType.Answered) {
            if (
                (this.formHelperService.isTextOrNumericQuestion(sourceQuestion) && answerValue) ||
                (this.formHelperService.isFileAttachmentElement(sourceQuestion) && mediaBankFileReferences.length)
            )
                return conditionAction === FormQuestionAction.Show;

            return conditionAction === FormQuestionAction.Hide;
        }

        if (condition.ConditionType === FormConditionType.NotAnswered) {
            if (
                (this.formHelperService.isTextOrNumericQuestion(sourceQuestion) && !answerValue) ||
                (this.formHelperService.isFileAttachmentElement(sourceQuestion) && !mediaBankFileReferences.length)
            )
                return conditionAction === FormQuestionAction.Show;

            return conditionAction === FormQuestionAction.Hide;
        }

        if (condition.ConditionType === FormConditionType.EqualsToPresetOption) {
            let isAnyOfOptionsChosen = condition.ConditionPresetOptionIds.some((optionId) => {
                return answerPresetOptionIds.some((answerOptionId) => optionId === answerOptionId);
            });

            if (this.formHelperService.isChoiceQuestion(sourceQuestion) && isAnyOfOptionsChosen)
                return conditionAction === FormQuestionAction.Show;

            return conditionAction === FormQuestionAction.Hide;
        }
    }

    private getSourceQuestionAnswer(question: IFormQuestionBase, answers: IFormAnswerBase[]): IFormAnswerBase {
        return answers.find((answer) => answer.FormQuestionUid === question.uid);
    }

    private getCondition(question: IFormQuestionBase, conditions: IFormCondition[]): IFormCondition {
        return conditions.find((condition) =>
            condition.Actions.some((action) => this.isQuestionTarget(question, action)),
        );
    }

    isSimplePredicate(condition: IFormCondition): boolean {
        return !condition.ChildItems.length;
    }

    getConditionDebugText(condition: IFormCondition, questions: IFormQuestion[]) {
        if (this.isSimplePredicate(condition)) {
            const sourceQuestion = questions.find((q) => q.uid === condition.sourceQuestionUid);
            if (!sourceQuestion) {
                return '?';
            }
            const questionName = sourceQuestion.Names.find((name) => name.Value);
            return questionName.Value ?? '?';
        } else {
            const childNames = condition.ChildItems.map((childItem) =>
                this.getConditionDebugText(childItem, questions),
            );
            const operatorName =
                condition.ConditionOperator === FiBooleanOperator.Or
                    ? ' or '
                    : condition.ConditionOperator === FiBooleanOperator.And
                      ? ' and '
                      : 'todo';
            return '(' + childNames.join(operatorName) + ')';
        }
    }

    getConditionWithChildren(condition: IFormCondition) {
        const result: IFormCondition[] = [condition];
        condition.ChildItems.forEach((childItem) => result.push(...this.getConditionWithChildren(childItem)));
        return result;
    }

    public concatenateChildItems(condition: IFormCondition, isOperatorChangeNeeded: boolean = true): IFormCondition[] {
        let newRootConditionChildItems: IFormCondition[] = [];
        const tempCondition = Object.assign({}, condition); // {...{}, ...condition};
        tempCondition.ChildItems.forEach((childItem: IFormCondition) => {
            const tempCondition: IFormCondition[] = childItem.ChildItems.length ? childItem.ChildItems : [childItem];
            newRootConditionChildItems = [...newRootConditionChildItems, ...tempCondition];
        });
        newRootConditionChildItems.forEach((newRootCondition, index) => {
            newRootConditionChildItems[index].Actions = undefined;
        });
        tempCondition.ChildItems = newRootConditionChildItems;
        if (isOperatorChangeNeeded)
            tempCondition.ChildItems.forEach((childItem: IFormCondition) => {
                childItem.ConditionOperator = FiBooleanOperator.Or;
            });
        return tempCondition.ChildItems;
    }

    private getAllConditionChildren(condition: IFormCondition, resultArr: IFormCondition[] = []) {
        condition.ChildItems.forEach((childCondition) => {
            if (childCondition.ChildItems.length) {
                this.getAllConditionChildren(childCondition, resultArr);
            } else {
                // note: we don't count condition with children since there is no needed data
                // in FiFormLogicConditionOperatorMenuComponent.convertConditionToComplex() we reset some properties
                // for a wrapper condition
                resultArr.push(childCondition);
            }
        });
        return resultArr;
    }

    isConditionComplete(condition: IFormCondition, formQuestions: IFormQuestion[]): boolean {
        if (!condition.ChildItems.length) {
            return (
                this.isConditionBaseComplete(condition, formQuestions) &&
                !this.isConditionActionsNotSet(condition, formQuestions)
            );
        }

        if (this.isConditionActionsNotSet(condition, formQuestions)) {
            return false;
        }

        return this.getAllConditionChildren(condition).every((flatCondition) => {
            return this.isConditionBaseComplete(flatCondition, formQuestions);
        });
    }

    isConditionBaseComplete(condition: IFormCondition, formQuestions: IFormQuestion[]) {
        const sourceQuestion: IFormQuestion = this.getQuestionByUid(condition.sourceQuestionUid, formQuestions);
        if (!isDefined(sourceQuestion) || !isDefined(condition.ConditionType)) {
            return false;
        }

        if (!this.isSimpleCondition(sourceQuestion)) {
            const presetOptions = condition.presetOptionUids
                .map((presetOptionUid) =>
                    this.getQuestionPresetOptionByUid(presetOptionUid, sourceQuestion.PresetOptions),
                )
                .filter((presetOption) => isDefined(presetOption));
            return !!presetOptions.length;
        }

        return true;
    }

    getConditionSummaryHTML(
        condition: IFormCondition,
        formQuestions: IFormQuestion[],
        selectedLanguageId: number,
    ): IConditionSummaryResult {
        if (!this.isConditionComplete(condition, formQuestions)) {
            return {
                isComplete: false,
                message: this.appTextService.get('condition.incomplete'),
            };
        }
        let summaryText: string = `<div class="condition-summary-row">${this.appTextService.get(
            'form.condition_summary_if',
        )} `;
        if (isDefined(condition.ConditionOperator)) {
            if (condition.ConditionOperator === FiBooleanOperator.Or) {
                this.concatenateChildItems(condition, false).forEach(
                    (childCondition: IFormCondition, index: number) => {
                        summaryText = summaryText.concat(
                            this.getSummaryRowText(childCondition, formQuestions, selectedLanguageId, index),
                        );
                    },
                );
            }
            if (condition.ConditionOperator === FiBooleanOperator.And) {
                if (
                    condition.ChildItems.every(
                        (childCondition) => childCondition.ConditionOperator === FiBooleanOperator.Or,
                    )
                ) {
                    summaryText = summaryText.concat(
                        this.getOldConditionTypeSummary(condition, formQuestions, selectedLanguageId),
                    );
                } else {
                    condition.ChildItems.forEach((childCondition: IFormCondition) => {
                        summaryText = summaryText.concat(
                            this.getSummaryRowText(childCondition, formQuestions, selectedLanguageId),
                        );
                    });
                }
            }
        } else {
            summaryText = summaryText.concat(this.getSummaryRowText(condition, formQuestions, selectedLanguageId));
        }
        summaryText = summaryText.concat(this.getSummaryActionsText(condition, formQuestions, selectedLanguageId));
        summaryText = summaryText.concat(`</div>`);

        return {
            isComplete: true,
            message: summaryText,
        };
    }

    private getOldConditionTypeSummary(
        condition: IFormCondition,
        formQuestions: IFormQuestion[],
        selectedLanguageId: number,
    ): string {
        // note: this is special case for an old condition where condition could be set as (Q1 OR Q2) AND (Q3 OR Q4)
        // the root condition always has two children. root condition operator set to FiBooleanOperator.And, and children's to FiBooleanOperator.Or
        let result = '';
        condition.ChildItems[0].ChildItems.forEach((childCondition, index) => {
            childCondition.ConditionOperator = FiBooleanOperator.Or;
            result = result.concat(this.getSummaryRowText(childCondition, formQuestions, selectedLanguageId, index));
        });
        result += ' ' + this.appTextService.get('form.condition_operator_and_if').toLowerCase() + ' ';
        condition.ChildItems[1].ChildItems.forEach((childCondition, index) => {
            // note: first child condition ConditionOperator must be set to FiBooleanOperator.And to get correct select label
            // in condition editor
            childCondition.ConditionOperator = index === 0 ? FiBooleanOperator.And : FiBooleanOperator.Or;
            result = result.concat(this.getSummaryRowText(childCondition, formQuestions, selectedLanguageId, index));
        });
        return result;
    }

    private getSummaryRowText(
        condition: IFormCondition,
        formQuestions: IFormQuestion[],
        selectedLanguageId,
        childConditionIndex?: number,
    ): string {
        let summaryText: string = '';

        if (isDefined(condition.ConditionOperator) && childConditionIndex !== 0)
            summaryText = summaryText.concat(
                ' ' +
                    `
                ${this.getConditionOperatorText(condition).toLowerCase()} ${this.appTextService.get('form.condition_summary_if')} `,
            );

        if (condition.ChildItems.length) {
            return summaryText;
        }

        const sourceQuestion: IFormQuestion = this.getQuestionByUid(condition.sourceQuestionUid, formQuestions);
        summaryText = summaryText.concat(`
            <span class="BrandedColor">
                <b>${this.getQuestionName(sourceQuestion, selectedLanguageId, formQuestions)}</b>
            </span> ${this.formHelperService.getIsConditionLabel(sourceQuestion).toLowerCase()} `);

        if (this.isSimpleCondition(sourceQuestion)) {
            summaryText = summaryText.concat(`
                <span>${this.getConditionTypeText(condition.ConditionType).toLowerCase()}</span></div>`);
        } else if (sourceQuestion.PresetOptions) {
            summaryText = summaryText.concat(`
                <span class="BrandedColor">
                    <b>${this.getPresetOptionsText(sourceQuestion, condition, selectedLanguageId)}</b>
                </span></div>`);
        }

        summaryText = summaryText.concat('<div class="condition-summary-row">');

        return summaryText;
    }

    getConditionOperatorText(condition: IFormCondition): string {
        return condition.ConditionOperator === FiBooleanOperator.Or
            ? this.appTextService.get('or')
            : this.appTextService.get('and');
    }

    private getSummaryActionsText(
        condition: IFormCondition,
        formQuestions: IFormQuestion[],
        selectedLanguageId: number,
    ): string {
        let summaryText = '';

        condition.Actions.forEach((conditionAction, index) => {
            summaryText = ' ' + summaryText.concat(`${this.getConditionActionSelectLabel(index).toLowerCase()} `);
            summaryText = summaryText.concat(
                `<span>${this.getActionLabel(conditionAction.Action).toLowerCase()}</span> `,
            );
            const targetQuestion = this.getQuestionByUid(conditionAction.targetQuestionUid, formQuestions);
            const questionName = this.getQuestionName(targetQuestion, selectedLanguageId, formQuestions);
            summaryText = summaryText.concat(`
                <span class="BrandedColor">
                    <b>${questionName}</b>
                </span> `);
        });

        return summaryText;
    }

    getConditionActionSelectLabel(conditionActionIndex: number): string {
        return this.appTextService.get(conditionActionIndex ? 'and' : 'form.condition_then');
    }

    private getPresetOptionsText(
        sourceQuestion: IFormQuestion,
        condition: IFormCondition,
        selectedLanguageId: number,
    ): string {
        return this.isSingleChoice(sourceQuestion)
            ? this.getSelectedSingleOptionName(condition, sourceQuestion, selectedLanguageId)
            : this.getSelectedMultipleOptionNames(condition, sourceQuestion, selectedLanguageId);
    }

    private getConditionTypeText(conditionType: FormConditionType): string {
        return this.appTextService.get(
            conditionType === FormConditionType.Answered
                ? 'form.condition_is_answered'
                : 'form.condition_is_not_answered',
        );
    }

    private getActionLabel(Action: FormQuestionAction): string {
        return this.appTextService.get(
            Action === FormQuestionAction.Show ? 'form.condition_then_show' : 'form.condition_then_hide',
        );
    }

    isConditionActionsNotSet(condition: IFormCondition, formQuestions: IFormQuestion[]): boolean {
        if (!condition.Actions?.length) return true;

        const invalidAction = condition.Actions.find((conditionAction) => {
            const isInvalidAction =
                !isDefined(conditionAction.Action) ||
                !isDefined(this.getQuestionByUid(conditionAction.targetQuestionUid, formQuestions));
            return isInvalidAction;
        });

        return !!invalidAction;
    }

    private isSimpleCondition(sourceQuestion: IFormQuestion): boolean {
        if (isDefined(sourceQuestion)) return this.formHelperService.isSimpleCondition(sourceQuestion);
    }

    private getSelectedSingleOptionName(
        condition: IFormCondition,
        sourceQuestion: IFormQuestion,
        selectedLanguageId: number,
    ): string {
        if (condition.presetOptionUid) {
            const selectedOption: IFormQuestionPresetOption = sourceQuestion.PresetOptions.find(
                (option: IFormQuestionPresetOption) => option.uid === condition.presetOptionUid,
            );
            return this.formHelperService.getPresetOptionName(selectedOption, selectedLanguageId, sourceQuestion);
        }
        return this.appTextService.get('condition.summary_selection_error_option');
    }

    private getSelectedMultipleOptionNames(
        condition: IFormCondition,
        sourceQuestion: IFormQuestion,
        selectedLanguageId: number,
    ): string {
        if (!!condition.presetOptionUids.length) {
            const selectedOptions: IFormQuestionPresetOption[] = sourceQuestion.PresetOptions.filter(
                (questionPresetOption: IFormQuestionPresetOption) => {
                    return condition.presetOptionUids.some((selectedPresetOptionUid: number) => {
                        return selectedPresetOptionUid === questionPresetOption.uid;
                    });
                },
            );

            const selectedOptionsNameArray: string[] = selectedOptions.map(
                (selectedOption: IFormQuestionPresetOption) => {
                    return this.formHelperService.getPresetOptionName(
                        selectedOption,
                        selectedLanguageId,
                        sourceQuestion,
                    );
                },
            );
            return selectedOptionsNameArray.join(', ');
        }
        return this.appTextService.get('condition.summary_selection_error_option');
    }

    private isSingleChoice(sourceQuestion: IFormQuestion): boolean {
        if (isDefined(sourceQuestion)) return this.formHelperService.isSingleChoiceQuestion(sourceQuestion);
    }

    private getQuestionName(
        question: IFormQuestion,
        selectedLanguageId: number,
        formQuestions: IFormQuestion[],
    ): string {
        return this.formHelperService.getQuestionName(question, selectedLanguageId, formQuestions);
    }

    private getQuestionByUid(questionUid: number, formQuestions: IFormQuestion[]): IFormQuestion {
        return formQuestions.find((question: IFormQuestion) => question.uid === questionUid);
    }

    private getQuestionPresetOptionByUid(
        questionPresetOptionUid: number,
        formQuestionPresetOptions: IFormQuestionPresetOption[],
    ): IFormQuestionPresetOption {
        return formQuestionPresetOptions.find((presetOption) => presetOption.uid === questionPresetOptionUid);
    }
}
